// Authorization gate — the single server-verified check between "the user is
// authenticated" and "render the Control Center". Wraps <AppContent/>.
//
// Authentication (a valid Cognito session) is handled upstream by
// CognitoAutoLoginGate. This gate then asks the backend GET /auth/me whether the
// authenticated user is actually PROVISIONED, and only mounts the app on a 200.
// A 403 (not provisioned / disabled) shows the access-denied screen; a transient
// error fails closed to a retry screen. It NEVER renders app content until the
// backend confirms access, so an unauthorized user can't see the shell.

import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/store";
import { setAccess } from "src/store/authSlice";
import { AccessErrorReason, checkAppAccess, routeToLogin } from "src/services/authorization";
import AuthScreen from "src/components/AutenticatingComp";
import AccessResult from "src/components/AccessResult";

const AuthorizationGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const dispatch = useDispatch<AppDispatch>();
	const status = useSelector((state: RootState) => state.authSlice.status);
	// Why the probe failed (server outage vs offline) — only meaningful when
	// status === "error"; drives the error-screen copy.
	const [errorReason, setErrorReason] = useState<AccessErrorReason>("server");

	const runCheck = useCallback(() => {
		dispatch(setAccess({ status: "checking" }));
		checkAppAccess().then(({ access, email, reason }) => {
			if (access === "unauthenticated") {
				// Session gone/invalid — bounce to login and keep the loader while the
				// navigation is in flight (don't flash the denied screen).
				routeToLogin();
				return;
			}
			if (reason) setErrorReason(reason);
			dispatch(setAccess({ status: access, email }));
		});
	}, [dispatch]);

	useEffect(() => {
		runCheck();
	}, [runCheck]);

	if (status === "allowed") return <>{children}</>;
	// Forbidden also gets a re-check: a mid-session 403 can be transient or
	// mistaken (any 403 flips this state globally) — without it the only
	// recovery from a false lockout was a manual full reload.
	if (status === "forbidden") return <AccessResult variant="forbidden" onRetry={runCheck} />;
	if (status === "error") return <AccessResult variant="error" reason={errorReason} onRetry={runCheck} />;
	// "checking" or "unauthenticated" (redirect in flight) → authenticating loader.
	return <AuthScreen profile />;
};

export default AuthorizationGate;
